import React, { useState, useRef, useEffect } from "react";
import { useResources } from "../../hooks/useResources";
import "./Pagination.scss";

interface PaginationProps {
  currentPage: number;
  totalPages: number;
  totalRecords: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  pageSizeOptions?: number[];
  disabled?: boolean;
}

const Pagination: React.FC<PaginationProps> = ({
  currentPage,
  totalPages,
  totalRecords,
  pageSize,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 25, 50, 100],
  disabled = false,
}) => {
  const { getResourceValue } = useResources();
  const [isPageSizeOpen, setIsPageSizeOpen] = useState(false);
  const pageSizeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pageSizeRef.current && !pageSizeRef.current.contains(event.target as Node)) {
        setIsPageSizeOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handlePageSizeSelect = (newSize: number) => {
    if (disabled) return;
    onPageSizeChange(newSize);
    setIsPageSizeOpen(false);
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      onPageChange(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      onPageChange(currentPage + 1);
    }
  };

  const startRow = totalRecords > 0 ? (currentPage - 1) * pageSize + 1 : 0;
  const endRow = Math.min(currentPage * pageSize, totalRecords);

  const pageSizeOptionsData = pageSizeOptions.map((size) => ({
    value: size,
    label: getResourceValue(`${size}_per_page`, `${size} per page`),
  }));

  const currentPageSizeLabel = pageSizeOptionsData.find(
    (opt) => opt.value === pageSize
  )?.label;

  return (
    <div className={`pagination-container ${disabled ? "pagination-container--disabled" : ""}`}>
      {/* Page Size Dropdown */}
      <div className="filter-dropdown" ref={pageSizeRef}>
        <div
          className={`filter-dropdown-content ${isPageSizeOpen ? "open" : ""} ${disabled ? "filter-dropdown-content--disabled" : ""}`}
          onClick={() => !disabled && setIsPageSizeOpen(!isPageSizeOpen)}
        >
          <div className="filter-dropdown-trigger">
            <span>{currentPageSizeLabel}</span>
            <i className="f-icon chevron-down-icon"></i>
          </div>
        </div>
        {isPageSizeOpen && (
          <div className="filter-dropdown-menu">
            {pageSizeOptionsData.map((option) => (
              <div
                key={option.value}
                className="filter-dropdown-item"
                onClick={() => handlePageSizeSelect(option.value)}
              >
                <span>{option.label}</span>
                {pageSize === option.value && (
                  <i className="f-icon check-icon fa-solid fa-check"></i>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Pagination Info with Navigation */}
      <div className="filter-dropdown pagination-control">
        <div className="pagination-info-text">
          {startRow}-{endRow} {getResourceValue("of", "of")} {totalRecords}
        </div>
        <div className="pagination-arrows">
          <button
            className="pagination-arrow"
            onClick={handlePreviousPage}
            disabled={currentPage === 1 || disabled}
            aria-label="Previous page"
          >
            <i className="f-icon chevron-left-icon fa-solid"></i>
          </button>
          <button
            className="pagination-arrow"
            onClick={handleNextPage}
            disabled={currentPage >= totalPages || disabled}
            aria-label="Next page"
          >
            <i className="f-icon chevron-right-icon fa-solid"></i>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Pagination;
